import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Keyboard } from 'swiper/modules';
import CustomVideo from './ui/CustomVideo';
import { isVideo } from '../utils';
// import ImageInfoMenu from './ImageInfoMenu';

const defaultGetName = (imageUrl = '') => {
    if (!imageUrl) return '';
    const clean = imageUrl.split('?')[0];
    const parts = clean.split('/');
    return parts[parts.length - 1] || imageUrl;
};

const ImageFullScreenViewer = ({
    isOpen,
    images = [],
    initialIndex = 0,
    onClose,
    onInfo,
    getName = defaultGetName,
    handleDownload,
    handleDelete
}: any) => {
    const [activeIndex, setActiveIndex] = useState(initialIndex);
    const [showInfoMenu, setShowInfoMenu] = useState(false);
    const swiperRef: any = useRef(null);

    // Keep in sync if the caller opens the viewer on a different index
    useEffect(() => {
        if (isOpen) {
            setActiveIndex(initialIndex);
            swiperRef.current?.slideTo(initialIndex, 0);
        }
    }, [isOpen, initialIndex]);

    // Lock background scroll while the viewer is open
    useEffect(() => {
        if (isOpen) {
            const prevOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => { document.body.style.overflow = prevOverflow; };
        }
    }, [isOpen]);

    const handleClose = useCallback(() => {
        onClose?.();
    }, [onClose]);

    // Escape key closes the viewer
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e: any) => { if (e.key === 'Escape') handleClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [isOpen, handleClose]);

    if (!isOpen || images.length === 0) return null;

    const total = images.length;
    const current = images[activeIndex];

    return (
        <div className="ifv-overlay" role="dialog" aria-modal="true">
            {/* Top bar */}
            <div className="ifv-topbar">
                <span className="ifv-topbar__filename">{getName(current?.image)}</span>

                <div className="ifv-topbar__right">
                    {/* <div className="lg-slide__info-wrap">
                        <button
                            className="lg-slide__info-btn"
                            onClick={e => {
                                e.stopPropagation();
                                setShowInfoMenu((p: any) => !p);
                            }}
                            aria-label="Image info"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="8" strokeWidth="2.5" />
                                <line x1="12" y1="12" x2="12" y2="16" />
                            </svg>
                        </button>

                        {showInfoMenu && (
                            <ImageInfoMenu
                                onClose={() => setShowInfoMenu(false)}
                                onDownload={() => {
                                    setShowInfoMenu(false)
                                    handleDownload?.(images?.[activeIndex])
                                }}
                                onInfo={() => {
                                    setShowInfoMenu(false);
                                    onInfo(images?.[activeIndex]);
                                }}
                                onDelete={() => {
                                    setShowInfoMenu(false);
                                    handleDelete(images?.[activeIndex])
                                }}
                            />
                        )}
                    </div> */}

                    <button className="ifv-close-btn" onClick={handleClose} aria-label="Close">
                        <img src='/assets/images/dismiss-cross-white.svg' alt='close' />
                    </button>
                </div>
            </div>

            {/* Main swiper */}
            <div className="ifv-stage">
                <Swiper
                    modules={[Navigation, Keyboard]}
                    keyboard={{ enabled: true }}
                    onSwiper={(s) => { swiperRef.current = s; }}
                    onSlideChange={(s) => setActiveIndex(s.activeIndex)}
                    initialSlide={initialIndex}
                    navigation={{ prevEl: '.ifv-nav-prev', nextEl: '.ifv-nav-next' }}
                    className="ifv-swiper"
                >
                    {images?.map((item: any, i: number) => {
                        return (
                            <SwiperSlide key={item.id ?? i}>
                                <div className="ifv-slide">
                                    {isVideo(item) ? (
                                        <CustomVideo
                                            className="ifv-slide__img"
                                            src={item?.image}
                                        />
                                    ) : (
                                        <img
                                            src={item.image}
                                            alt={getName(item.image)}
                                            className="ifv-slide__img"
                                        />
                                    )}
                                </div>
                            </SwiperSlide>
                        );
                    })}
                </Swiper>

                {total > 1 && (
                    <>
                        <button className="ifv-nav-btn ifv-nav-prev" aria-label="Previous">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                        </button>
                        <button className="ifv-nav-btn ifv-nav-next" aria-label="Next">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                                <polyline points="9 18 15 12 9 6" />
                            </svg>
                        </button>
                    </>
                )}

                {total > 1 && (
                    <div className="ifv-counter">{activeIndex + 1} of {total}</div>
                )}
            </div>
        </div>
    );
};

export default ImageFullScreenViewer;